// Snap-to-grid uses raw `canvas.on('object:moving' | 'object:scaling', ...)`
// because the editor bus does not expose live transform events — snapping must
// run mid-drag, before fabric finalizes the transform, so an `object:modified`
// hook would be too late. Documented exception per criteria doc, section
// "Centralized typed event bus".
import { fabric } from 'fabric';
import type { EditorPlugin } from 'modules/fabric-editor';
import type { FabricEditor } from '../core/fabric-editor';
import { FabricPluginName } from './plugin-name';

export interface GridFacade {
  getStep(): number;
  setStep(step: number): void;
  isVisible(): boolean;
  setVisible(visible: boolean): void;
  isSnapping(): boolean;
  setSnap(snap: boolean): void;
  rebuild(): void;
  /**
   * One-shot snap of `target` (default: current selection, expanded for a
   * multi-select) to the grid — position always, plus size for axis-aligned
   * objects (rotated objects only get repositioned, matching onScaling's
   * skip-rotated rule). Batches into one history step when the history
   * plugin is installed.
   */
  fitToGrid(target?: fabric.Object): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    grid?: GridFacade;
  }
}

export interface GridPluginOptions {
  step?: number;
  visible?: boolean;
  snap?: boolean;
  color?: string;
  lineWidth?: number;
}

const MIN_STEP = 2;

export function gridPlugin(options: GridPluginOptions = {}): EditorPlugin {
  let step = Math.max(1, options.step ?? 10);
  let visible = options.visible ?? true;
  let snap = options.snap ?? true;
  const color = options.color ?? 'rgba(0, 0, 0, 0.35)';
  const lineWidth = options.lineWidth ?? 0.5;

  return {
    name: FabricPluginName.GRID,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;
      let group: fabric.Group | undefined;

      const roundByGrid = (n: number) => {
        const r = Math.round(n / step) * step;
        return r <= 0 ? Math.ceil(n / step) * step : r;
      };

      const lineProps = {
        stroke: color,
        strokeWidth: lineWidth,
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        lockUniScaling: true,
        objectCaching: false,
        excludeFromExport: true,
      } as const;

      const removeGroup = () => {
        if (!group) return;
        canvas.remove(group);
      };

      const buildGroup = () => {
        const { width, height } = editor.resolution;
        if (width <= 0 || height <= 0 || step < MIN_STEP) {
          group = undefined;
          return;
        }

        const lines: fabric.Line[] = [];
        for (let x = 0; x <= width; x += step) {
          lines.push(new fabric.Line([x, 0, x, height], lineProps));
        }
        for (let y = 0; y <= height; y += step) {
          lines.push(new fabric.Line([0, y, width, y], lineProps));
        }

        group = new fabric.Group(lines, {
          left: 0,
          top: 0,
          width,
          height,
          selectable: false,
          evented: false,
          hoverCursor: 'default',
          lockMovementX: true,
          lockMovementY: true,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
          lockUniScaling: true,
          objectCaching: false,
          excludeFromExport: true,
        });
        group.data = { name: 'grid', isSave: false, gridStep: step, resolution: { width, height } };
      };

      const rebuild = () => {
        removeGroup();
        group = undefined;
        if (!visible) return;
        buildGroup();
        if (!group) return;
        canvas.add(group);
        canvas.sendToBack(group);
        canvas.requestRenderAll();
      };

      const onMoving = (e: fabric.IEvent) => {
        if (!snap) return;
        const target = e.target;
        if (!target) return;
        const coord = target.getPointByOrigin('left', 'top');
        if (!coord) return;
        target.set({ left: roundByGrid(coord.x), top: roundByGrid(coord.y) });
      };

      const onScaling = (e: fabric.IEvent) => {
        if (!snap || !e.transform) return;
        const active = e.target;
        if (!active) return;
        // The snap math below is axis-aligned: it treats `left + scaledWidth` as
        // a horizontal span and `top + scaledHeight` as vertical, then rewrites
        // left/top/scaleX/scaleY. For a rotated object none of that holds —
        // left/top is not the visual top-left and getScaledWidth() is the
        // local-axis width — so snapping drags the object sideways mid-scale.
        // Skip rotated objects (any angle that isn't a multiple of 360); Fabric's
        // native scaling already pins the opposite edge correctly.
        const normalizedAngle = (((active.angle ?? 0) % 360) + 360) % 360;
        if (normalizedAngle > 0.01 && normalizedAngle < 359.99) return;
        if (active.left === undefined || active.top === undefined) return;
        if (active.width === undefined || active.height === undefined) return;
        if (active.scaleX === undefined || active.scaleY === undefined) return;

        const strokeWidth = active.strokeWidth ?? 1;
        const width = active.getScaledWidth();
        const height = active.getScaledHeight();
        const corner = e.transform.corner;

        if (['tl', 'ml', 'bl'].includes(corner)) {
          const tl = roundByGrid(active.left);
          active.scaleX = (width + active.left - tl) / (active.width + strokeWidth);
          active.left = tl;
        } else if (['tr', 'mr', 'br'].includes(corner)) {
          const tl = roundByGrid(active.left + width);
          active.scaleX = (tl - active.left) / (active.width + strokeWidth);
        }

        if (['tl', 'mt', 'tr'].includes(corner)) {
          const tt = roundByGrid(active.top);
          active.scaleY = (height + active.top - tt) / (active.height + strokeWidth);
          active.top = tt;
        } else if (['bl', 'mb', 'br'].includes(corner)) {
          const tt = roundByGrid(active.top + height);
          active.scaleY = (tt - active.top) / (active.height + strokeWidth);
        }

        active.scaleX = (active.scaleY >= 0 ? 1 : -1) * Math.max(Math.abs(active.scaleX), 0.001);
        active.scaleY = (active.scaleY >= 0 ? 1 : -1) * Math.max(Math.abs(active.scaleY), 0.001);
      };

      const resolveTargets = (target?: fabric.Object): fabric.Object[] =>
        target ? [target] : canvas.getActiveObjects();

      const fitOneToGrid = (obj: fabric.Object) => {
        const coord = obj.getPointByOrigin('left', 'top');
        if (!coord) return;
        const newLeft = roundByGrid(coord.x);
        const newTop = roundByGrid(coord.y);
        obj.set({ left: newLeft, top: newTop });

        const normalizedAngle = (((obj.angle ?? 0) % 360) + 360) % 360;
        const isAxisAligned = normalizedAngle <= 0.01 || normalizedAngle >= 359.99;
        if (
          isAxisAligned &&
          obj.width !== undefined &&
          obj.height !== undefined &&
          obj.scaleX !== undefined &&
          obj.scaleY !== undefined
        ) {
          const strokeWidth = obj.strokeWidth ?? 0;
          const right = roundByGrid(newLeft + obj.getScaledWidth());
          const bottom = roundByGrid(newTop + obj.getScaledHeight());
          const nextScaleX = Math.max(right - newLeft, step) / (obj.width + strokeWidth);
          const nextScaleY = Math.max(bottom - newTop, step) / (obj.height + strokeWidth);
          obj.set({ scaleX: nextScaleX, scaleY: nextScaleY });
        }

        obj.setCoords();
      };

      canvas.on('object:moving', onMoving);
      canvas.on('object:scaling', onScaling);

      // History undo/redo replaces canvas state via loadFromJSON, which wipes
      // the grid (excluded from export). Rebuild on the typed event so the
      // grid plugin self-heals without coupling history to it.
      const offCanvasLoaded = editor.on('canvas:loaded', () => rebuild());

      rebuild();

      const facade: GridFacade = {
        getStep: () => step,
        setStep(value) {
          const next = Math.max(1, value);
          if (next === step) return;
          step = next;
          rebuild();
        },
        isVisible: () => visible,
        setVisible(value) {
          if (value === visible) return;
          visible = value;
          if (visible) {
            rebuild();
          } else {
            removeGroup();
            group = undefined;
            canvas.requestRenderAll();
          }
        },
        isSnapping: () => snap,
        setSnap(value) {
          snap = value;
        },
        // Why: FabricEditor.setResolution does not emit a bus event, so callers
        // must invoke rebuild() manually after changing resolution.
        rebuild,
        fitToGrid(target) {
          const targets = resolveTargets(target);
          if (targets.length === 0) return;
          const run = () => {
            for (const obj of targets) fitOneToGrid(obj);
            canvas.requestRenderAll();
            for (const obj of targets) editor.emit('object:modified', { target: obj });
          };
          if (editor.history) editor.history.batch(run);
          else run();
        },
      };
      editor.grid = facade;

      (facade as GridFacade & { __off?: () => void }).__off = () => {
        canvas.off('object:moving', onMoving as never);
        canvas.off('object:scaling', onScaling as never);
        offCanvasLoaded();
        removeGroup();
        group = undefined;
      };
    },
    dispose(editor: FabricEditor) {
      const facade = editor.grid as (GridFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      editor.grid = undefined;
    },
  };
}
